import {
  ActionIcon,
  Checkbox,
  ColorInput,
  Table,
  TextInput,
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { modals } from '@mantine/modals'
import {
  IconCheckbox,
  IconEdit,
  IconSquareX,
  IconTrash,
} from '@tabler/icons-react'
import { useEffect } from 'react'

import useEditTag from '../../hooks/useEditTag.hook'
import { type ITag, TagUpdateMutationData } from '../../tag.types'
import DeleteTagContent from '../deleteTagModal/deleteTagModal.component'
import styles from './tags-table-row.module.scss'

const ConditionalFieldRenderer = (
  isEditable: boolean,
  key: keyof TagUpdateMutationData,
  value: string | boolean | undefined,
  getInputProps: UseFormReturnType<TagUpdateMutationData>['getInputProps'],
  isLoading: boolean,
  isBoolean: boolean = false,
) => {
  return isEditable ? (
    isBoolean ? (
      <Checkbox
        radius="4px"
        classNames={{ body: styles['table-td-boolean'] }}
        {...getInputProps(key)}
        defaultChecked={value as boolean}
        disabled={isLoading}
      />
    ) : key === 'color' ? (
      <ColorInput label="Color" {...getInputProps(key)} size="xs" />
    ) : (
      <TextInput
        disabled={isLoading}
        placeholder={value as string}
        {...getInputProps(key)}
      />
    )
  ) : (
    String(value)
  )
}

const TagsTableRow = ({
  tag,
  onMutationSuccess,
}: {
  tag: ITag
  onMutationSuccess: () => void
}) => {
  const { rowValuesForm, handleUpdateTag, updateTagMutationStatus } =
    useEditTag()

  useEffect(() => {
    rowValuesForm.reset()
  }, [tag])

  const isLoading = updateTagMutationStatus === 'pending'
  const isEditMode = !!rowValuesForm.getTransformedValues()['id']

  const handleDelete = (tagId: string) => {
    modals.open({
      centered: true,
      withCloseButton: true,
      size: 'md',
      children: (
        <DeleteTagContent onMutationSuccess={onMutationSuccess} tagId={tagId} />
      ),
    })
  }

  return (
    <Table.Tr key={tag.id}>
      <Table.Td>
        {ConditionalFieldRenderer(
          isEditMode,
          'name',
          tag.name,
          rowValuesForm.getInputProps,
          isLoading,
        )}
      </Table.Td>
      <Table.Td>
        {ConditionalFieldRenderer(
          isEditMode,
          'isProtected',
          tag.isProtected,
          rowValuesForm.getInputProps,
          isLoading,
          true,
        )}
      </Table.Td>
      <Table.Td>
        {ConditionalFieldRenderer(
          isEditMode,
          'isHided',
          tag.isHided,
          rowValuesForm.getInputProps,
          isLoading,
          true,
        )}
      </Table.Td>
      <Table.Td>
        {ConditionalFieldRenderer(
          isEditMode,
          'isNotEligible',
          tag.isNotEligible,
          rowValuesForm.getInputProps,
          isLoading,
          true,
        )}
      </Table.Td>
      <Table.Td bg={tag.color}>
        {ConditionalFieldRenderer(
          isEditMode,
          'color',
          tag.color,
          rowValuesForm.getInputProps,
          isLoading,
        )}
      </Table.Td>
      <Table.Td>
        {isEditMode ? (
          <>
            <ActionIcon
              variant="subtle"
              color="green.9"
              loading={isLoading}
              onClick={() => handleUpdateTag(onMutationSuccess)}
            >
              <IconCheckbox size={18} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red.9"
              loading={isLoading}
              onClick={rowValuesForm.reset}
            >
              <IconSquareX size={16} />
            </ActionIcon>
          </>
        ) : (
          <>
            <ActionIcon
              variant="subtle"
              color="blue.9"
              onClick={() => rowValuesForm.setFieldValue('id', tag?.id ?? '')}
            >
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red.9"
              onClick={() => handleDelete(tag.id as string)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </>
        )}
      </Table.Td>
    </Table.Tr>
  )
}

export default TagsTableRow
